import type { FC } from "react";
import { useMemo, useState } from "react";

import classNames from "classnames";
import type { SwiperClass } from "swiper/react";
import { Swiper, SwiperSlide } from "swiper/react";

import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { Button } from "@jti/core-components/components/Button";
import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";

import "swiper/css";

import { Typography } from "../Typography";

import type { FlavourCarouselCardProps } from "./components/FlavourCarouselCard/FlavourCarouselCard";
import { FlavourCarouselCard, PRODUCT_GRADIENTS } from "./components/FlavourCarouselCard/FlavourCarouselCard";
import { FlavourCarouselThumbnailsList } from "./components/FlavourCarouselThumbnailsList/FlavourCarouselThumbnailsList";

import type { Flavour } from "./FlavourCarouselContext";
import { FlavourCarouselContext } from "./FlavourCarouselContext";

import styles from "./FlavourCarousel.module.scss";

type FlavourCarouselProps = {
  backgroundImage: string;
  title?: string;
  description?: string;
  isInversed?: boolean;
  isThumbnailsCarouselHidden?: boolean;
  flavourCards: FlavourCarouselCardProps[];
  redirectLink?: {
    label?: string;
    url?: string;
    target?: "_blank" | "_self";
  };
};

export const FlavourCarousel: FC<FlavourCarouselProps> = ({
  backgroundImage,
  description,
  redirectLink,
  title,
  isThumbnailsCarouselHidden,
  isInversed,
  flavourCards,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);

  function changeSlide(index: number): void {
    setActiveIndex(index);
    swiper?.slideTo(index);
  }

  const thumbnailsList: Flavour[] = useMemo(
    () =>
      flavourCards.map(({ title, image, backgroundColor, backgroundGradient, theme }, index) => ({
        title,
        index,
        imageUrl: image.src,
        color: (backgroundGradient ? PRODUCT_GRADIENTS[backgroundGradient] : undefined) || backgroundColor || "",
        theme,
      })),
    [flavourCards]
  );
  const value = useMemo(() => ({ isInversed }), [isInversed]);

  return (
    <FlavourCarouselContext.Provider value={value}>
      <div className={styles.container}>
        <div className={styles.content}>
          {title && (
            <Typography as={"h3"} variant="h1" className={styles.title}>
              {title}
            </Typography>
          )}
          {description && <div className={styles.description}>{description}</div>}
          {redirectLink?.label && redirectLink?.url && (
            <Button
              size="sm"
              href={redirectLink.url}
              variant="text-muted"
              target={redirectLink.target}
              iconAfter={LongArrowForwardIcon}
            >
              {redirectLink.label}
            </Button>
          )}
        </div>
        <div className={styles.carouselWrapper}>
          <div
            className={classNames(styles.mainCarouselWrapper, {
              [styles.inversed]: isInversed,
            })}
          >
            <Swiper
              className={styles.carousel}
              onSwiper={setSwiper}
              onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
            >
              {flavourCards.map((flavourCard, index) => (
                <SwiperSlide key={index}>
                  <div className={styles.slide}>
                    <FlavourCarouselCard {...flavourCard} />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <ImageBehaviour className={styles.backgroundImage} src={backgroundImage} alt="" width={618} height={493} />
          </div>
          {!isThumbnailsCarouselHidden && (
            <FlavourCarouselThumbnailsList
              selectFlavour={changeSlide}
              activeIndex={activeIndex}
              thumbnails={thumbnailsList}
            />
          )}
        </div>
      </div>
    </FlavourCarouselContext.Provider>
  );
};
